const appOverlayTarget = '#alkokh-overlay-target'

/**
 * ⚠️ WHY MENUS CLOSE ON SCROLL ON TOUCH DEVICES.
 *
 * Vuetify's `VMenu` defaults to `scrollStrategy: 'reposition'`, which recomputes the menu's
 * placement on every scroll event. In the phone shell `main.mobile-shell__content` is the scroller
 * (`mobile-shell.scss`), so an open dropdown re-ran the whole flip/clamp pass per frame while the
 * page moved under it — the panel jumped and resized instead of tracking its field. `reposition`
 * also never dismisses, so scrolling a field off-screen stranded an open menu.
 *
 * `'close'` is the platform-native behaviour and cannot drift. Scrolling INSIDE the list does not
 * close it: `bindScroll` binds to `document` plus the scroll parents of the ACTIVATOR, and element
 * scroll events do not bubble.
 *
 * Evaluated once at module load — a device does not grow a mouse mid-session, and Vuetify defaults
 * are static anyway. Desktop keeps `reposition` exactly as before.
 */
const isCoarsePointer = typeof window !== 'undefined'
  && (window.matchMedia?.('(pointer: coarse)').matches || 'ontouchstart' in window)

export default {
  IconBtn: {
    icon: true,
    // Icons are PRIMARY (violet), not a grey on-surface tone.
    //
    // Two earlier values both produced grey-on-white: `default`, and then a high-emphasis
    // `on-surface` binding — `on-surface` IS near-black in this theme, so raising its opacity only
    // ever darkened grey, which is why the toolbar still looked "white only". Using the brand
    // colour gives thin tabler strokes real presence against a white app bar and matches the
    // violet already used by the page's own accents.
    //
    // Components that set their own `color` (AppSoundToggle's warning/primary states, Shortcuts'
    // high-emphasis inner button) still override this — a default is only a fallback.
    color: 'primary',
    variant: 'text',
  },
  VAlert: {
    density: 'comfortable',
    VBtn: {
      color: undefined,
    },
  },
  VAvatar: {
    // ℹ️ Remove after next release
    variant: 'flat',
  },
  VBadge: {
    // set v-badge default color to primary
    color: 'primary',
  },
  VBtn: {
    // set v-btn default color to primary
    color: 'primary',
  },
  VChip: {
    label: true,
  },
  VDataTable: {
    VPagination: {
      showFirstLastPage: true,
      firstIcon: 'tabler-chevrons-left',
      lastIcon: 'tabler-chevrons-right',
    },
  },
  VDataTableServer: {
    VPagination: {
      showFirstLastPage: true,
      firstIcon: 'tabler-chevrons-left',
      lastIcon: 'tabler-chevrons-right',
    },
  },
  VExpansionPanel: {
    expandIcon: 'tabler-chevron-right',
    collapseIcon: 'tabler-chevron-right',
  },
  VExpansionPanelTitle: {
    expandIcon: 'tabler-chevron-right',
    collapseIcon: 'tabler-chevron-right',
  },
  VList: {
    color: 'primary',
    density: 'compact',
    VCheckboxBtn: {
      density: 'compact',
    },
    VListItem: {
      ripple: false,
      VAvatar: {
        size: 40,
      },
    },
  },
  VDialog: {
    attach: appOverlayTarget,
    /*
     * ⚠️ A DIALOG CLOSES ONLY THROUGH ITS OWN CANCEL / ✕. A tap on the scrim beside a create/edit
     * form, a stray Escape, or (formerly) a sideways swipe threw the half-filled form away. Every
     * dialog in the app has an explicit close control, so nothing is trapped; an outside tap now
     * plays Vuetify's small "bounce" instead.
     *
     * ⚠️ A BOUND `:persistent="busy"` OVERRIDES THIS DEFAULT and makes the dialog dismissible again
     * whenever the expression is false. Write a static `persistent` (or leave the prop off).
     * Bottom sheets are menus, not forms, and keep tap-outside / drag-to-dismiss.
     */
    persistent: true,
  },
  VOverlay: {
    attach: appOverlayTarget,
  },
  VMenu: {
    attach: appOverlayTarget,
    offset: '2px',
    scrollStrategy: isCoarsePointer ? 'close' : 'reposition',
  },
  VPagination: {
    density: 'comfortable',
    variant: 'tonal',
  },
  VTabs: {
    // set v-tabs default color to primary
    color: 'primary',
    density: 'comfortable',
    VSlideGroup: {
      showArrows: true,
    },
  },
  VTooltip: {
    attach: appOverlayTarget,
    // set v-tooltip default location to top
    location: 'top',
  },
  VCheckboxBtn: {
    color: 'primary',
  },
  VCheckbox: {
    // set v-checkbox default color to primary
    color: 'primary',
    density: 'comfortable',
    hideDetails: 'auto',
  },
  VRadioGroup: {
    color: 'primary',
    density: 'comfortable',
    hideDetails: 'auto',
  },
  VRadio: {
    density: 'comfortable',
    hideDetails: 'auto',
  },
  VSelect: {
    variant: 'outlined',
    color: 'primary',
    density: 'comfortable',
    hideDetails: 'auto',
    menuProps: {
      attach: appOverlayTarget,
    },
    VChip: {
      label: true,
    },
  },
  VRangeSlider: {
    // set v-range-slider default color to primary
    color: 'primary',
    trackSize: 6,
    thumbSize: 22,
    density: 'comfortable',
    thumbLabel: true,
    hideDetails: 'auto',
  },
  VRating: {
    // set v-rating default color to primary
    color: 'warning',
  },
  VProgressLinear: {
    height: 6,
    roundedBar: true,
    rounded: true,
    bgColor: 'rgba(var(--v-track-bg))',
  },
  VSlider: {
    // set v-range-slider default color to primary
    color: 'primary',
    thumbLabel: true,
    hideDetails: 'auto',
    thumbSize: 22,
    trackSize: 6,
    elevation: 4,
  },
  VTextField: {
    variant: 'outlined',
    density: 'comfortable',
    color: 'primary',
    hideDetails: 'auto',
  },
  VAutocomplete: {
    variant: 'outlined',
    color: 'primary',
    density: 'comfortable',
    hideDetails: 'auto',
    menuProps: {
      attach: appOverlayTarget,
      contentClass: 'app-autocomplete__content v-autocomplete__content',
    },
    VChip: {
      label: true,
    },
  },
  VCombobox: {
    variant: 'outlined',
    density: 'comfortable',
    color: 'primary',
    hideDetails: 'auto',
    menuProps: {
      attach: appOverlayTarget,
    },
    VChip: {
      label: true,
    },
  },
  VFileInput: {
    variant: 'outlined',
    density: 'comfortable',
    color: 'primary',
    hideDetails: 'auto',
  },
  VTextarea: {
    variant: 'outlined',
    density: 'comfortable',
    color: 'primary',
    hideDetails: 'auto',
  },
  VSnackbar: {
    VBtn: {
      density: 'comfortable',
    },
  },
  VSwitch: {
    // set v-switch default color to primary
    inset: true,
    color: 'primary',
    hideDetails: 'auto',
    ripple: false,
  },
  VNavigationDrawer: {
    touchless: false,
  },
}
